import * as React from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import Dropdown from "@Tmp/Dropdown";
import { LoadingLine } from "..";
import Search from "@Tmp/Search";
import {
    FilterCategoryBoxProps,
    FilterDropdownProps,
    ProductListTag,
    ProductListToolbarProps,
} from "./ProductList.types";
import SelectedTags from "./Toolbar/SelectedTags";
import Tags from "./Toolbar/Tags";
import MainTags from "./Toolbar/MainTags";
import {
    jsonEncode,
    pushRemoveQueryStringParam,
    pushUpdateUrlQueryStringParam,
    removeQueryStringParam,
} from "@TmpApp/Utils";
import { useIsDevice, useSettings } from "@Tmp/Hooks";
import Transition from "@Tmp/Transition";

const Toolbar: React.FC<ProductListToolbarProps> = (props) => {
    const [search, setSearch] = useState(props.search);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterCategoryBoxesOpen, setFilterCategoryBoxesOpen] = useState<number>(null);

    const isMobile = useIsDevice("mobile");

    const resetFilters = (): void => {
        props.resetLazyLoading();
        window.history.pushState({}, document.title, window.location.href.split("?")[0]);
        props.setData((prev) => ({
            ...prev,
            page: 1,
            perpage: parseInt(props.settings.pagination_per_page.size),
            search: "",
            selected_tags: [],
        }));
    };

    const onTagChange = (tag: ProductListTag): void => {
        props.resetLazyLoading();

        let json = [...props.selected_tags];
        if (json.includes(tag.id)) {
            json = tmp.array_remove_value(json, tag.id);
        } else {
            json.push(tag.id);
        }

        if (!json.length) {
            pushRemoveQueryStringParam("tags");
        } else {
            pushUpdateUrlQueryStringParam("tags", jsonEncode(json));
        }

        props.setData((prev) => ({ ...prev, selected_tags: json, search: "", page: 1 }));

        removeQueryStringParam("q");
        removeQueryStringParam("p-page");
    };

    const viewAllMainTags = (): void => {
        props.setData((prev) => ({ ...prev, selected_tags: [], page: 1 }));
        pushRemoveQueryStringParam("tags");
        removeQueryStringParam("p-page");
    };

    const onMainTagClick = (tag: ProductListTag): void => {
        props.resetLazyLoading();

        let json = [...props.selected_tags];
        if (json.includes(tag.id)) {
            pushRemoveQueryStringParam("tags");
            json = [];
        } else {
            const value = [tag.id];
            pushUpdateUrlQueryStringParam("tags", tmp.utils.json.encode(value));
            json = value;
        }

        props.setData((prev) => ({ ...prev, selected_tags: json, page: 1 }));

        removeQueryStringParam("p-page");
    };

    const onSearch = (q) => {
        props.resetLazyLoading();

        pushUpdateUrlQueryStringParam({
            "p-page": 1,
            q: q,
        });

        props.setData((prev) => ({ ...prev, page: 1, search: q }));
    };

    const renderFilterCategoryBoxes = (): React.ReactElement => {
        return (
            <>
                {props.tag_categories
                    .filter((item) => props.settings.filter_box_categories?.includes(item.id))
                    .sort((a, b) => {
                        if (a.name > b.name) {
                            return 1;
                        }
                        if (a.name < b.name) {
                            return -1;
                        }
                        return 0;
                    })
                    .map((item, index) => (
                        <FilterCategoryBox
                            key={index}
                            settings={props.settings}
                            tags={props.tags}
                            tags_raw={props.tags_raw}
                            selected_tags={props.selected_tags}
                            setData={props.setData}
                            onTagChange={onTagChange}
                            searchTags={props.searchTags}
                            resetFilters={resetFilters}
                            open={filterCategoryBoxesOpen}
                            setOpen={setFilterCategoryBoxesOpen}
                            category={item}
                        />
                    ))}
            </>
        );
    };

    const renderFilterDropdown = (): React.ReactElement => {
        return (
            <FilterDropdown
                settings={props.settings}
                open={filtersOpen}
                setOpen={setFiltersOpen}
                tags={props.tags}
                tags_raw={props.tags_raw}
                tag_categories={props.tag_categories}
                selected_tags={props.selected_tags}
                isMobile={isMobile}
                loading={props.loading}
                onTagChange={onTagChange}
                searchTags={props.searchTags}
                setData={props.setData}
                resetFilters={resetFilters}
            />
        );
    };

    return (
        <div className="tmp-product-list__toolbar">
            <div className="tmp-product-list__toolbar__row">
                <div className="tmp-product-list__toolbar__filters-container">
                    {props.settings.display_filter_boxes === "yes" && (
                        <button
                            className="tmp-btn tmp-product-list__toolbar__filters-button"
                            type="button"
                            onClick={() => setFiltersOpen(!filtersOpen)}
                        >
                            {props.settings.filters_button_text}
                        </button>
                    )}
                    {isMobile ? (
                        createPortal(
                            renderFilterDropdown(),
                            document.getElementsByTagName("body")[0]
                        )
                    ) : (
                        <>
                            {renderFilterDropdown()}
                            {renderFilterCategoryBoxes()}
                        </>
                    )}
                </div>
                {props.settings.display_search === "yes" && (
                    <Search
                        search={search}
                        setSearch={setSearch}
                        placeholder={props.settings.search_placeholder}
                        onSearch={onSearch}
                        classes={{
                            container: ["tmp-product-list__search-container"],
                            search: ["tmp-product-list__search"],
                            button: ["tmp-btn", "tmp-product-list__search-button"],
                        }}
                    >
                        {props.settings.search_button_text}
                    </Search>
                )}
            </div>
            <div className="tmp-product-list__toolbar__row tmp-product-list__toolbar__row-center">
                {props.settings?.main_tags?.length > 0 && (
                    <MainTags
                        settings={props?.settings}
                        showAllText={props?.settings?.main_tags_show_all_text}
                        tags_raw={props?.tags_raw}
                        selected_tags={props.selected_tags}
                        onMainTagClick={onMainTagClick}
                        viewAllMainTags={viewAllMainTags}
                        resetFilters={resetFilters}
                    />
                )}
            </div>
            <div className="tmp-product-list__toolbar__row">
                {props.selected_tags.filter((tag) =>
                    props.settings.main_tags.includes(tag) ? false : true
                ).length > 0 && (
                    <SelectedTags
                        resetFiltersText={props.settings.filters_reset_text}
                        tags_raw={props.tags_raw}
                        selected_tags={props.selected_tags.filter((tag) =>
                            props.settings.main_tags.includes(tag) ? false : true
                        )}
                        onTagChange={onTagChange}
                        resetFilters={resetFilters}
                    />
                )}
            </div>
        </div>
    );
};

const FilterDropdown: React.FC<FilterDropdownProps> = (props) => {
    const tmpSettings = useSettings();

    const [currentCategoryId, setCurrentCategoryId] = useState<number>(null);

    const handleBodyOverflow = (value: string): void => {
        if (window.innerWidth > tmpSettings.breakpoints.mobile.value && value === "hidden") {
            return;
        }

        jQuery("html, body").css({ overflowY: value });
    };

    const dropdownAnimation = props.isMobile ? "slide" : "fade";

    const renderTags = (item, index): React.ReactElement => {
        const tags = props.tags.filter(
            (tag) => tag.categories.includes(item.id) && !props.settings.main_tags.includes(tag.id)
        );

        if (tags.length < 1) {
            return;
        }

        return (
            <Transition
                key={item.id}
                type="slide"
                in={item.id === currentCategoryId}
                direction="right"
            >
                <Tags
                    settings={props.settings}
                    tags_search=""
                    tags={tags}
                    tags_raw={props.tags_raw}
                    selected_tags={props.selected_tags}
                    disableSearch={tags.length < 20}
                    categoryName={item.name}
                    setCurrentCategoryId={setCurrentCategoryId}
                    setData={props.setData}
                    onTagChange={props.onTagChange}
                    searchTags={props.searchTags}
                    resetFilters={props.resetFilters}
                />
            </Transition>
        );
    };

    return (
        <Dropdown
            open={props.open}
            animation={dropdownAnimation}
            backdrop={dropdownAnimation === "slide"}
            direction="left"
            onClose={() => {
                props.setOpen(false);
                setCurrentCategoryId(null);
            }}
            onExit={() => {
                handleBodyOverflow("");
            }}
            onEnter={() => {
                handleBodyOverflow("hidden");
            }}
            classes={{
                container: "tmp-product-list__toolbar__filters-dropdown-container",
            }}
        >
            {props.loading && <LoadingLine />}
            <Transition type="slide" in={currentCategoryId === null}>
                <div className="tmp-product-list__toolbar__filter-container">
                    <div className="tmp-product-list__tags__header">
                        <div className="tmp-product-list__tags__header-main-row">
                            <h4>{props.settings?.filters_button_text}</h4>
                        </div>
                        <SelectedTags
                            resetFiltersText={props.settings.filters_reset_text}
                            tags_raw={props.tags_raw}
                            selected_tags={props.selected_tags.filter((tag) =>
                                props.settings?.main_tags.includes(tag) ? false : true
                            )}
                            onTagChange={props.onTagChange}
                            resetFilters={props.resetFilters}
                        />
                    </div>
                    {props.tag_categories
                        .filter(
                            (item) =>
                                !props.settings.filter_box_categories?.includes(item.id) ||
                                props.isMobile
                        )
                        .sort((a, b) => {
                            if (a.name > b.name) {
                                return 1;
                            }
                            if (a.name < b.name) {
                                return -1;
                            }
                            return 0;
                        })
                        .map((item, index) => {
                            const tagsCount = props.tags.filter(
                                (tag) =>
                                    tag.categories.includes(item.id) &&
                                    !props.settings.main_tags.includes(tag.id)
                            ).length;

                            if (tagsCount < 1) {
                                return;
                            }

                            const selectedCount = props.tags.filter(
                                (tag) =>
                                    tag.categories.includes(item.id) &&
                                    !props.settings.main_tags.includes(tag.id) &&
                                    props.selected_tags.some(
                                        (selectedTag) => selectedTag === tag.id
                                    )
                            );

                            return (
                                <div
                                    key={index}
                                    className="tmp-product-list__tags__category"
                                    onClick={() => setCurrentCategoryId(item.id)}
                                >
                                    <span>
                                        {item.name +
                                            (selectedCount.length
                                                ? " (" +
                                                  selectedCount.length +
                                                  (props.settings?.filters_selected_text || "") +
                                                  ")"
                                                : "")}
                                    </span>
                                    <i className="fas fa-angle-right" />
                                </div>
                            );
                        })}
                </div>
            </Transition>
            {props.tag_categories
                .filter(
                    (item) =>
                        !props.settings.filter_box_categories?.includes(item.id) || props.isMobile
                )
                .map((item, index) => renderTags(item, index))}
        </Dropdown>
    );
};

const FilterCategoryBox: React.FC<FilterCategoryBoxProps> = (props) => {
    const tags = props.tags.filter(
        (tag) =>
            tag.categories.includes(props.category.id) && !props.settings.main_tags.includes(tag.id)
    );

    if (tags.length < 1) {
        return null;
    }

    return (
        <>
            <button
                className="tmp-btn tmp-product-list__toolbar__filters-button"
                type="button"
                onClick={() =>
                    props.setOpen(props.category.id === props.open ? null : props.category.id)
                }
            >
                {props.category.name}
            </button>
            <Dropdown
                open={props.open === props.category.id}
                animation="fade"
                onClose={() => props.setOpen(null)}
                classes={{
                    container: "tmp-product-list__toolbar__filters-dropdown-container",
                }}
            >
                <Tags
                    settings={props.settings}
                    tags_search=""
                    tags={tags}
                    tags_raw={props.tags_raw}
                    selected_tags={props.selected_tags}
                    disableSearch={tags.length < 20}
                    categoryName={props.category.name}
                    setCurrentCategoryId={props.setOpen}
                    setData={props.setData}
                    onTagChange={props.onTagChange}
                    searchTags={props.searchTags}
                    resetFilters={props.resetFilters}
                />
            </Dropdown>
        </>
    );
};

export default Toolbar;
